/**
 * @file MilkPowderContractHistoryModal.js
 * @version 2.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-06-08
 * @modified 2026-10-08
 * @description Full contract sale-price history for one customer + product.
 *              Table (newest first, each price's From and To as DD/MM/YY — 2.1.0,
 *              they were months) sits above a line chart plotting the per-bag
 *              sale price (a point at each From date) against the per-bag cost
 *              — drawn from the finer WEEKLY cost series — so the margin gap is
 *              visible week to week. A 12-month window with back/forward
 *              chevrons pages through history; every recorded sale price and
 *              cost week in view is dotted. Rows can be edited (the price
 *              window, through `onEdit`) or deleted; CURRENT marks the price
 *              covering today.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import useThemeColors from '../hooks/useThemeColors';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler
} from 'chart.js';
import { API_URL } from '../config';
import Modal from './Modal';
import LoadingSpinner from './LoadingSpinner';
import ConfirmDialog from './ConfirmDialog';
import ChartWindowNav from './ChartWindowNav';
import useTimeWindow from '../hooks/useTimeWindow';
import { formatCurrency } from '../utils/currencyFormatter';
import { formatDateDDMMYY } from '../utils/dateFormatters';
import { rangeOf, coversDay, formatRange, toDay } from '../utils/milkPowderDates';
import './TableStyles.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

const parseDay = (d) => new Date(String(d).slice(0, 10) + 'T00:00:00');
const shortDate = d => d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });

// Value in force at time x from an ascending [{ts, v}] series (last v with ts<=x).
function valueAt(series, x) {
  let v = null;
  for (const p of series) { if (p.ts <= x) v = p.v; else break; }
  return v;
}

function MilkPowderContractHistoryModal({ pair, onClose, onChanged, onEdit }) {
  const [rows, setRows] = useState([]);            // recorded rows (table)
  const [saleMonths, setSaleMonths] = useState([]); // monthly sale series (chart)
  const [costWeeks, setCostWeeks] = useState([]);   // weekly per-bag cost series (chart)
  const [loading, setLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, message: '', onConfirm: null });
  const hoverRef = useRef(null); // { x } timestamp under the cursor

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const base = `${API_URL}/api/pricing/milk-powder/contract-prices/${pair.account_id}/${pair.inventory_item_id}`;
      const [hRes, sRes] = await Promise.all([
        fetch(`${base}/history`, { credentials: 'include' }),
        fetch(`${base}/series`, { credentials: 'include' })
      ]);
      const h = await hRes.json();
      const s = await sRes.json();
      setRows(Array.isArray(h) ? h : []);
      setSaleMonths(Array.isArray(s?.months) ? s.months : []);
      setCostWeeks(Array.isArray(s?.weeklyCost) ? s.weeklyCost : []);
    } catch (e) {
      console.error('Error loading contract history:', e);
      setRows([]); setSaleMonths([]); setCostWeeks([]);
    } finally {
      setLoading(false);
    }
  }, [pair.account_id, pair.inventory_item_id]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const handleDelete = (row) => {
    setConfirmDialog({
      isOpen: true,
      message: `Delete the price for ${formatRange(row)} (${formatCurrency(row.sale_price)})?`,
      onConfirm: async () => {
        setConfirmDialog({ isOpen: false, message: '', onConfirm: null });
        try {
          await fetch(`${API_URL}/api/pricing/milk-powder/contract-prices/${row.id}`, { method: 'DELETE', credentials: 'include' });
          await loadHistory();
          if (onChanged) onChanged();
        } catch (e) { console.error('Error deleting contract price:', e); }
      }
    });
  };

  // Ascending sale (monthly) + cost (weekly) series, each carrying the last known
  // value forward across gaps. Sale gaps draw dotted; cost is a separate dashed
  // line. Both are kept full-span so the window can page back a year at a time.
  const built = useMemo(() => {
    // One point per recorded price, at its From date (the server sends only
    // recorded prices now; a null is still skipped). Real points, a smooth
    // curve through them, and nothing drawn where nothing was recorded — like
    // the cost chart in the item modal.
    const saleAsc = [...saleMonths]
      .filter(r => r.sale_price != null)
      .map(r => ({ ts: parseDay(r.valid_from || r.price_month).getTime(), recorded: true, v: parseFloat(r.sale_price) }))
      .filter(p => Number.isFinite(p.v))
      .sort((a, b) => a.ts - b.ts);

    // One carry-forward point at the current month so the line reaches "now" at
    // the price still in force — the same ending the item modal's chart uses.
    // Not recorded, so it draws no dot.
    if (saleAsc.length) {
      const thisMonth = new Date(); thisMonth.setDate(1); thisMonth.setHours(0, 0, 0, 0);
      const ts = thisMonth.getTime();
      if (saleAsc[saleAsc.length - 1].ts < ts) {
        saleAsc.push({ ts, recorded: false, v: saleAsc[saleAsc.length - 1].v });
      }
    }

    const costAsc = [...costWeeks]
      .map(r => ({ ts: parseDay(r.week_commencing).getTime(), recorded: !!r.recorded, v: r.cost_per_bag != null ? parseFloat(r.cost_per_bag) : null }))
      .filter(p => p.v != null)
      .sort((a, b) => a.ts - b.ts);

    return { saleAsc, costAsc };
  }, [saleMonths, costWeeks]);

  const extent = useMemo(() => {
    const ts = [...built.saleAsc, ...built.costAsc].map(p => p.ts);
    return ts.length ? [Math.min(...ts), Math.max(...ts)] : [null, null];
  }, [built]);
  const win = useTimeWindow(extent);

  /* Re-read on a theme change: a canvas keeps the colours it was painted
     with, so without this the chart stayed in the old palette. */
  const themeColors = useThemeColors({
    textPrimary: '--text-primary',
    textPrimaryRgb: '--text-primary-rgb',
    textSecondary: '--text-secondary',
    bgTertiary: '--bg-tertiary'
  }, {
    textPrimary: '#fff',
    textPrimaryRgb: '255, 255, 255',
    textSecondary: '#c2c2c2',
    bgTertiary: '#2f2f2f'
  });

  const chart = useMemo(() => {
    const { saleAsc, costAsc } = built;
    if (saleAsc.length < 1 && costAsc.length < 1) return null;
    const { startTs, endTs } = win;

    const { textPrimary, textPrimaryRgb, textSecondary, bgTertiary } = themeColors;

    const RAINBOW = ['255, 122, 89', '255, 93, 177', '155, 109, 255', '74, 168, 255'];
    const rainbowGradient = (c, area, alpha) => {
      const g = c.createLinearGradient(area.left, 0, area.right, 0);
      RAINBOW.forEach((rgb, i) => g.addColorStop(i / (RAINBOW.length - 1), `rgba(${rgb}, ${alpha})`));
      return g;
    };
    const rainbowBorder = (ctx) => {
      const { chartArea, ctx: c } = ctx.chart;
      if (!chartArea) return `rgba(${RAINBOW[0]}, 1)`;
      return rainbowGradient(c, chartArea, 1);
    };
    const DOT_DASH = [2, 4];

    // Y-range from what's visible in the window (both series).
    const visible = [...saleAsc, ...costAsc].filter(p => p.ts >= startTs && p.ts <= endTs);
    const ys = (visible.length ? visible : [...saleAsc, ...costAsc]).map(p => p.v).filter(v => v != null && Number.isFinite(v));
    const yLo = ys.length ? Math.min(...ys) : 0;
    const yHi = ys.length ? Math.max(...ys) : 1;
    const yPad = (yHi - yLo || yHi || 1) * 0.18;

    // First-of-month ticks across the window.
    const months = [];
    const cur = new Date(startTs); cur.setDate(1); cur.setHours(0, 0, 0, 0);
    while (cur.getTime() <= endTs) { months.push(cur.getTime()); cur.setMonth(cur.getMonth() + 1); }
    const stepN = Math.max(1, Math.ceil(months.length / 9));
    const ticks = months.filter((_, i) => i % stepN === 0);

    const inWin = (x) => x >= startTs && x <= endTs;

    const data = {
      datasets: [
        {
          label: 'Sale price / bag',
          data: saleAsc.map(p => ({ x: p.ts, y: p.v, recorded: p.recorded })),
          borderColor: rainbowBorder,
          fill: 'start',
          backgroundColor: (ctx) => {
            const { chart } = ctx; const { ctx: c, chartArea } = chart;
            if (!chartArea) return 'transparent';
            return rainbowGradient(c, chartArea, 0.22);
          },
          pointRadius: (ctx) => { const p = ctx.raw; return (p && p.recorded && inWin(p.x)) ? 2.8 : 0; },
          pointHoverRadius: 0,
          pointBackgroundColor: textPrimary,
          pointBorderWidth: 0,
          borderWidth: 1.5,
          borderCapStyle: 'round',
          tension: 0.4,
          spanGaps: true
        },
        {
          label: 'Cost / bag',
          data: costAsc.map(p => ({ x: p.ts, y: p.v, recorded: p.recorded })),
          borderColor: textSecondary,
          fill: false,
          pointRadius: (ctx) => { const p = ctx.raw; return (p && p.recorded && inWin(p.x)) ? 2.2 : 0; },
          pointHoverRadius: 0,
          pointBackgroundColor: textSecondary,
          pointBorderWidth: 0,
          borderWidth: 1.5,
          borderDash: DOT_DASH,
          borderCapStyle: 'round',
          spanGaps: true,
          tension: 0.4
        }
      ]
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', axis: 'x', intersect: false },
      layout: { padding: { top: 40, left: 0, right: 0, bottom: 0 } },
      plugins: {
        legend: {
          display: true, position: 'top', align: 'end',
          labels: {
            color: textSecondary, font: { size: 10 }, usePointStyle: true, boxWidth: 18,
            generateLabels: () => ([
              { text: 'Sale price', fillStyle: 'rgba(255, 93, 177, 1)', strokeStyle: 'rgba(255, 93, 177, 1)', lineWidth: 2, pointStyle: 'line' },
              { text: 'Cost (weekly)', fillStyle: textSecondary, strokeStyle: textSecondary, lineWidth: 2, lineDash: [2, 4], pointStyle: 'line' }
            ])
          }
        },
        tooltip: { enabled: false },
        title: { display: false }
      },
      // Snap the crosshair to the nearest x across both series (within window).
      onHover: (e, els, chart) => {
        if (!e || e.x == null) { if (hoverRef.current != null) { hoverRef.current = null; chart.draw(); } return; }
        // Collect in-window candidate points from both series, then pick nearest.
        const cands = [];
        for (let di = 0; di < 2; di++) {
          const meta = chart.getDatasetMeta(di);
          const ds = chart.data.datasets[di];
          if (!meta || !meta.data || !ds) continue;
          for (let i = 0; i < meta.data.length; i++) {
            const el = meta.data[i]; const dp = ds.data[i];
            if (!el || !dp || !inWin(dp.x)) continue;
            cands.push({ x: dp.x, px: el.x });
          }
        }
        let bestX = null, bd = Infinity;
        for (const c of cands) { const d = Math.abs(c.px - e.x); if (d < bd) { bd = d; bestX = c.x; } }
        if (hoverRef.current !== bestX) { hoverRef.current = bestX; chart.draw(); }
      },
      scales: {
        x: {
          type: 'linear', min: startTs, max: endTs,
          grid: { display: false }, border: { display: false },
          afterBuildTicks: (axis) => { axis.ticks = ticks.map(v => ({ value: v })); },
          ticks: { color: textSecondary, font: { size: 9 }, maxRotation: 0, autoSkip: false, callback: (val) => new Date(val).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' }) }
        },
        y: { display: false, min: yLo - yPad, max: yHi + yPad }
      }
    };

    // Crosshair: vertical line at the hovered x, dots on each line, and a pill
    // showing date · sale · cost · margin (each value the one in force at x).
    const crosshair = {
      id: 'mpcCrosshair',
      afterDatasetsDraw(chart) {
        const hx = hoverRef.current;
        if (hx == null) return;
        const { ctx, chartArea, scales } = chart;
        const px0 = scales.x.getPixelForValue(hx);
        const sale = valueAt(saleAsc, hx);
        const cost = valueAt(costAsc, hx);
        const saleRecorded = saleAsc.some(p => p.ts === hx && p.recorded);
        const margin = (sale != null && cost != null) ? sale - cost : null;

        ctx.save();
        const g = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
        g.addColorStop(0, `rgba(${textPrimaryRgb}, 0.55)`);
        g.addColorStop(1, `rgba(${textPrimaryRgb}, 0)`);
        ctx.beginPath(); ctx.setLineDash([4, 4]); ctx.lineWidth = 1; ctx.strokeStyle = g;
        ctx.moveTo(px0, chartArea.bottom); ctx.lineTo(px0, chartArea.top); ctx.stroke(); ctx.setLineDash([]);

        if (cost != null) { const y = scales.y.getPixelForValue(cost); ctx.beginPath(); ctx.fillStyle = textSecondary; ctx.arc(px0, y, 3, 0, Math.PI * 2); ctx.fill(); }
        if (sale != null) { const y = scales.y.getPixelForValue(sale); ctx.beginPath(); ctx.fillStyle = textPrimary; ctx.arc(px0, y, 3.5, 0, Math.PI * 2); ctx.fill(); }

        const l1 = shortDate(new Date(hx));
        const saleStr = sale == null ? '—' : (saleRecorded ? formatCurrency(sale) : `(${formatCurrency(sale)})`);
        const l2 = `Sale ${saleStr}  ·  Cost ${cost != null ? formatCurrency(cost) : '—'}` +
          (margin != null ? `  ·  ${margin >= 0 ? '+' : ''}${formatCurrency(margin)}` : '');
        ctx.font = '600 11px sans-serif'; const w1 = ctx.measureText(l1).width;
        ctx.font = '500 11px sans-serif'; const w2 = ctx.measureText(l2).width;
        const padX = 9, h = 36;
        const w = Math.max(w1, w2) + padX * 2;
        let px = px0 - w / 2;
        px = Math.max(chartArea.left, Math.min(px, chartArea.right - w));
        const py = Math.max(0, chartArea.top - h - 2);
        const r = 10;
        ctx.beginPath();
        ctx.moveTo(px + r, py);
        ctx.arcTo(px + w, py, px + w, py + h, r);
        ctx.arcTo(px + w, py + h, px, py + h, r);
        ctx.arcTo(px, py + h, px, py, r);
        ctx.arcTo(px, py, px + w, py, r);
        ctx.closePath();
        ctx.fillStyle = bgTertiary; ctx.fill();
        ctx.textBaseline = 'middle'; ctx.textAlign = 'left';
        ctx.fillStyle = textPrimary; ctx.font = '600 11px sans-serif';
        ctx.fillText(l1, px + padX, py + 12);
        ctx.fillStyle = textSecondary; ctx.font = '500 11px sans-serif';
        ctx.fillText(l2, px + padX, py + 25);
        ctx.restore();
      }
    };

    return { data, options, plugins: [crosshair] };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [built, win.startTs, win.endTs, themeColors]);

  const fromOf = (r) => rangeOf(r)?.from || '';
  const desc = [...rows].sort((a, b) => (fromOf(a) < fromOf(b) ? 1 : fromOf(a) > fromOf(b) ? -1 : 0));
  const today = toDay(new Date());

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={`Contract Prices — ${pair.company_name} · ${pair.item_name}`}
      size="md"
      className="milk-powder-history"
    >
      {loading ? <LoadingSpinner /> : (
        <>
          <div className="table-container-short">
            <table className="data-table">
              <thead>
                <tr>
                  <th>From</th>
                  <th>To</th>
                  <th className="text-right">Sale / bag</th>
                  <th className="text-right">Cost / bag</th>
                  <th className="text-right">Margin</th>
                  <th>Notes</th>
                  <th className="actions-col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {desc.length === 0 ? (
                  <tr><td colSpan="7" className="text-center">No price history yet</td></tr>
                ) : (
                  desc.map((row) => {
                    const range = rangeOf(row);
                    const sale = parseFloat(row.sale_price);
                    const cost = row.cost_per_bag != null ? parseFloat(row.cost_per_bag) : null;
                    const margin = cost != null ? sale - cost : null;
                    return (
                      <tr key={row.id}>
                        <td>{range ? formatDateDDMMYY(range.from) : '—'}{coversDay(row, today) && <span className="current-cost-badge">CURRENT</span>}</td>
                        <td>{range ? formatDateDDMMYY(range.to) : '—'}</td>
                        <td className="text-right"><div className="price">{formatCurrency(sale)}</div></td>
                        <td className="text-right">{cost != null ? formatCurrency(cost) : '—'}</td>
                        <td className="text-right" style={{ color: margin == null ? 'var(--text-muted)' : (margin >= 0 ? 'var(--color-success)' : 'var(--color-error)'), fontWeight: 600 }}>
                          {margin != null ? `${margin >= 0 ? '+' : ''}${formatCurrency(margin)}` : '—'}
                        </td>
                        <td style={{ color: 'var(--text-muted)', fontSize: 'var(--font-sm)' }}>{row.notes || ''}</td>
                        <td className="actions-col">
                          <div className="actions-cell">
                            <button className={`btn-icon${onEdit ? '' : ' btn-disabled'}`} onClick={() => onEdit && onEdit(row)} disabled={!onEdit}
                              title={onEdit ? 'Edit this price — its dates, sale price and notes' : 'Edit from the Milk Powder screen'}>
                              <span className="material-symbols-rounded">edit</span>
                            </button>
                            <button className="btn-icon" onClick={() => handleDelete(row)} title="Delete this price">
                              <span className="material-symbols-rounded">delete</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {chart && (
            <div className="mp-history-chart">
              <Line data={chart.data} options={chart.options} plugins={chart.plugins} />
              <ChartWindowNav
                canBack={win.canBack}
                canForward={win.canForward}
                onBack={win.goBack}
                onForward={win.goForward}
                label={win.label}
              />
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
      />
    </Modal>
  );
}

export default MilkPowderContractHistoryModal;
